import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../data/mockData';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import { fetchBlogPosts, fetchGroupChatPublicHighlights } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Newspaper, Clock, User, ArrowRight, Calendar, Users, MessageSquare, ShieldCheck, TrendingUp, Hash } from 'lucide-react';

const CATEGORY_COLORS = {
  lifestyle: '#66BB6A', platform: '#5C6BC0', nutrition: '#EF5350',
  technology: '#42A5F5', community: '#FF9800',
};

const TOPIC_LABELS = {
  sleep: 'Sleep', headspace: 'Headspace', interaction: 'Interaction',
  exercise: 'Exercise', limit_exposure: 'Limit Exposure', diet: 'Diet',
  environment: 'Environment', discovery: 'Discovery',
  genetics_biomarkers: 'Genetics & Biomarkers',
  general_longevity: 'General Longevity',
};

function sentimentTone(score) {
  if (score >= 0.2) return { label: 'Positive', color: '#2E7D32', bg: '#E8F5E9' };
  if (score <= -0.2) return { label: 'Tense', color: '#C62828', bg: '#FFEBEE' };
  return { label: 'Neutral', color: '#A65500', bg: '#FFF3E0' };
}

export default function BlogPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [groupBlog, setGroupBlog] = useState(null);

  useEffect(() => {
    fetchBlogPosts().then(data => { setPosts(data); setLoading(false); }).catch(() => setLoading(false));
    fetchGroupChatPublicHighlights().then(setGroupBlog).catch(() => setGroupBlog({ rooms: [], latest_digest: null }));
  }, []);

  const categories = ['all', ...new Set(posts.map(p => p.category))];
  const filtered = filter === 'all' ? posts : posts.filter(p => p.category === filter);

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh' }}>
      <SiteHeader />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(180deg, #F9F6F0 0%, #E8ECD7 50%, #F9F6F0 100%)', padding: '64px 20px 48px', textAlign: 'center' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2, textTransform: 'uppercase' }}>BLOG</span>
          <h1 data-testid="blog-title" style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 900, color: COLORS.textPrimary, lineHeight: 1.15, letterSpacing: -1, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>
            Health & Longevity Insights
          </h1>
          <p style={{ fontSize: 'clamp(16px, 2vw, 18px)', color: COLORS.textSecondary, lineHeight: 1.6, marginTop: 16 }}>
            Tips, guides, and stories from our team and community to help you live longer and better.
          </p>
          {user && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
              <Link
                to="/dashboard/chat/groups"
                data-testid="start-group-blog-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '12px 22px', borderRadius: 9999, backgroundColor: COLORS.primary, color: '#FFF', textDecoration: 'none', fontSize: 14, fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
              >
                <MessageSquare size={16} /> Start a Group Blog
              </Link>
              <Link
                to="/dashboard/profile"
                data-testid="manage-invite-prefs-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '12px 18px', borderRadius: 9999, border: `1.5px solid ${COLORS.primary}`, color: COLORS.primary, textDecoration: 'none', fontSize: 13, fontWeight: 700, backgroundColor: 'transparent' }}
              >
                Allow Invitations
              </Link>
            </div>
          )}
          {!user && (
            <div style={{ marginTop: 22 }}>
              <Link to="/login" data-testid="start-group-blog-signin" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '12px 22px', borderRadius: 9999, backgroundColor: COLORS.primary, color: '#FFF', textDecoration: 'none', fontSize: 14, fontWeight: 700 }}>
                <MessageSquare size={16} /> Sign in to Start a Group Blog
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Filter */}
      {groupBlog && (groupBlog.rooms?.length > 0 || groupBlog.latest_digest) && (
        <section data-testid="group-blog-section" style={{ padding: '0 20px 36px' }}>
          <div style={{ maxWidth: 1000, margin: '0 auto', backgroundColor: COLORS.surface, borderRadius: 22, padding: '28px 28px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <MessageSquare size={20} color={COLORS.primary} />
              <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2, textTransform: 'uppercase' }}>GROUP BLOG</span>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>What the community is talking about</h2>
            <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.5, marginTop: 6, marginBottom: 18 }}>
              Public, AI-moderated group discussions on the SHIELDED longevity pillars. Sentiment and keywords are auto-indexed; transcripts are stored under our privacy policy.
            </p>

            {groupBlog.latest_digest && groupBlog.latest_digest.totals?.messages > 0 && (
              <div data-testid="group-blog-digest" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18, padding: 16, borderRadius: 14, background: 'linear-gradient(135deg, #EDF1E5 0%, #F9F6F0 100%)' }}>
                <div style={{ flex: '1 1 220px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: COLORS.primary, textTransform: 'uppercase' }}>Latest Weekly Digest</div>
                  <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>
                    {(groupBlog.latest_digest.period_start || '').slice(0,10)} → {(groupBlog.latest_digest.period_end || '').slice(0,10)}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                    <div><strong style={{ color: COLORS.textPrimary, fontSize: 18 }}>{groupBlog.latest_digest.totals?.messages || 0}</strong> <span style={{ fontSize: 11, color: COLORS.textSecondary }}>messages</span></div>
                    <div><strong style={{ color: '#2E7D32', fontSize: 18 }}>{groupBlog.latest_digest.totals?.approved || 0}</strong> <span style={{ fontSize: 11, color: COLORS.textSecondary }}>approved</span></div>
                    <div><strong style={{ color: '#A65500', fontSize: 18 }}>{groupBlog.latest_digest.totals?.redacted || 0}</strong> <span style={{ fontSize: 11, color: COLORS.textSecondary }}>moderated</span></div>
                  </div>
                </div>
                {(groupBlog.latest_digest.top_keywords || []).length > 0 && (
                  <div style={{ flex: '2 1 320px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: COLORS.primary, textTransform: 'uppercase', marginBottom: 6 }}>Trending Keywords</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(groupBlog.latest_digest.top_keywords || []).slice(0, 14).map((k, i) => (
                        <span key={i} style={{ padding: '4px 10px', borderRadius: 9999, backgroundColor: '#FFF', color: COLORS.primary, fontSize: 12, fontWeight: 600, border: `1px solid ${COLORS.primary}20`, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Hash size={11} />{k.keyword}<span style={{ color: COLORS.textSecondary, fontWeight: 400 }}>· {k.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(groupBlog.rooms || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 16px', color: COLORS.textSecondary, fontSize: 14 }}>
                No public group discussions yet. <Link to="/dashboard/chat/groups" style={{ color: COLORS.primary, fontWeight: 700, textDecoration: 'none' }}>Start the first one →</Link>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                {(groupBlog.rooms || []).slice(0, 9).map((r) => {
                  const tone = sentimentTone(r.avg_sentiment || 0);
                  return (
                    <Link
                      key={r.room_id}
                      to={`/dashboard/chat/groups/${r.room_id}`}
                      data-testid={`group-blog-room-${r.room_id}`}
                      style={{ textDecoration: 'none', backgroundColor: '#FFF', borderRadius: 14, padding: 16, border: `1px solid ${COLORS.border}50`, display: 'block' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 1 }}>{TOPIC_LABELS[r.topic] || r.topic}</span>
                        <span style={{ padding: '2px 8px', borderRadius: 9999, backgroundColor: tone.bg, color: tone.color, fontSize: 10, fontWeight: 700 }}>{tone.label}</span>
                      </div>
                      <h3 style={{ fontSize: 16, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>{r.name}</h3>
                      {r.description && <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.45, margin: '6px 0 10px' }}>{r.description.length > 110 ? r.description.slice(0, 110) + '…' : r.description}</p>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: COLORS.textSecondary }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Users size={11} /> {r.member_count}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><TrendingUp size={11} /> {r.recent_message_count} / 14d</span>
                      </div>
                      {r.keywords?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
                          {r.keywords.slice(0, 3).map((k, i) => (
                            <span key={i} style={{ padding: '2px 8px', borderRadius: 8, backgroundColor: '#EDF1E5', color: COLORS.primary, fontSize: 11, fontWeight: 600 }}>#{k}</span>
                          ))}
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: 18, padding: '10px 14px', borderRadius: 10, backgroundColor: '#FFF8E1', border: '1px solid #F0D77A', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <ShieldCheck size={16} color="#B58A00" style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: 12, color: '#7A5C00', lineHeight: 1.5 }}>
                Every room is AI-moderated in real time and gated by privacy + key-term consent. Open a room to read the conversation in full.
              </span>
            </div>

            <div style={{ marginTop: 14, textAlign: 'center' }}>
              <Link to="/dashboard/chat/groups" data-testid="group-blog-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 9999, backgroundColor: COLORS.primary, color: '#FFF', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
                Open Group Chats <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Filter */}
      <section style={{ padding: '0 20px 32px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            {categories.map(c => (
              <button
                key={c}
                data-testid={`blog-filter-${c}`}
                onClick={() => setFilter(c)}
                style={{
                  padding: '8px 18px', borderRadius: 9999, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
                  backgroundColor: filter === c ? COLORS.primary : COLORS.surface,
                  color: filter === c ? '#FFF' : COLORS.textSecondary,
                  transition: 'all 0.2s',
                }}
              >
                {c === 'all' ? 'All Posts' : c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts */}
      <section style={{ padding: '0 20px 56px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>Loading posts...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
              {filtered.map((post, i) => {
                const catColor = CATEGORY_COLORS[post.category] || COLORS.primary;
                return (
                  <div key={post.post_id} data-testid={`blog-post-${post.post_id}`} className="hover-lift" style={{ backgroundColor: COLORS.surface, borderRadius: 20, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'transform 0.3s, box-shadow 0.3s', display: 'flex', flexDirection: 'column' }}>
                    {/* Color strip */}
                    <div style={{ height: 4, backgroundColor: catColor }} />
                    <div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, backgroundColor: `${catColor}18`, color: catColor, textTransform: 'capitalize', alignSelf: 'flex-start', marginBottom: 14 }}>
                        {post.category}
                      </span>
                      <h3 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 8, lineHeight: 1.3 }}>{post.title}</h3>
                      <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.6, flex: 1 }}>{post.excerpt}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 14, borderTop: `1px solid ${COLORS.border}40` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <User size={14} color={COLORS.textSecondary} />
                            <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{post.author}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={14} color={COLORS.textSecondary} />
                            <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{post.read_time}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Calendar size={14} color={COLORS.textSecondary} />
                          <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{new Date(post.published).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
